/* eslint-disable */
import { Typography } from '@material-ui/core';

/* eslint-disable */
import {
  Backdrop,
  Box,
  CircularProgress,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
} from '@material-ui/core';
import { CgChevronRight, CgChevronLeft } from 'react-icons/cg';
import { graphql } from 'gatsby';
import { makeStyles } from '@material-ui/core/styles';
import * as React from 'react';
import slugify from '@sindresorhus/slugify';

import { Filters } from '../components/filters';
import { Layout } from '../components/layout';
import { ProductCard } from '../components/product-card';
import { SearchProvider } from '../context/search-provider';
import { Spinner } from '../components/progress';
import { getCurrencySymbol } from '../utils/format-price';
import { getValuesFromQueryString, useProductSearch } from '../utils/hooks';
import {
  visuallyHidden,
  search,
  results,
  pagination,
  paginationButton,
  progressStyle,
  resultsStyle,
  filterStyle,
  filterButton,
  filterTitle,
  modalOpen,
  activeFilters,
  filterWrap,
} from './search-page.module.css';
import CrossIcon from '../icons/cross';
import FilterIcon from '../icons/filter';
import SearchBar from '../components/search-bar';
import WhiteSpace from '../components/styled/white-space';

const useStyles = makeStyles((theme) => ({
  root: {
    minHeight: 'calc(100vh)',
    backgroundColor: 'white',
  },
  productsContainer: {
    minHeight: 'calc(100vh)',
    padding: theme.spacing(10, 0),
    paddingBottom: theme.spacing(10, 0),
  },
  filters: {
    position: 'sticky',
    top: 0,
    right: 0,
    backgroundColor: theme.motorX.lightGray,
    height: 'calc(100vh)',
    zIndex: 0,
    overflowY: 'scroll',
  },
  backdrop: {
    zIndex: theme.zIndex.drawer + 1,
    color: theme.palette.primary.main,
  },
}));

export const query = graphql`
  query {
    meta: allShopifyProduct {
      productTypes: distinct(field: productType)
      tags: distinct(field: tags)
      vendors: distinct(field: vendor)
    }
    products: allShopifyProduct(limit: 12, sort: { fields: title }) {
      edges {
        node {
          title
          vendor
          productType
          handle
          priceRangeV2 {
            minVariantPrice {
              currencyCode
              amount
            }
            maxVariantPrice {
              currencyCode
              amount
            }
          }
          id
          images {
            gatsbyImageData(width: 300, layout: CONSTRAINED, aspectRatio: 1)
          }
        }
      }
    }
  }
`;

function SearchPage({
  data: {
    meta: { productTypes, vendors, tags },
    products,
  },
  location,
}) {
  // These default values come from the page query string
  const queryParams = getValuesFromQueryString(location.search);
  const [filters, setFilters] = React.useState(queryParams);
  const [sortKey, setSortKey] = React.useState(queryParams.sortKey);
  // We clear the hash when searching, we want to make sure the next page will be fetched due the #more hash.
  const shouldLoadNextPage = React.useRef(false);

  const classes = useStyles();

  // This modal is only used on mobile
  const [showModal, setShowModal] = React.useState(false);

  const {
    data,
    isFetching,
    filterCount,
    hasNextPage,
    hasPreviousPage,
    fetchNextPage,
    fetchPreviousPage,
  } = useProductSearch(
    filters,
    {
      allProductTypes: productTypes,
      allVendors: vendors,
      allTags: tags,
    },
    sortKey,
    false,
    24, // Products per page
  );

  // If we're using the default filters, use the products from the Gatsby data layer.
  // Otherwise, use the data from search.
  const isDefault = !data;
  const productList =
    (isDefault ? products.edges : data?.products?.edges) ?? [];

  // Scroll up when navigating
  React.useEffect(() => {
    if (!showModal) {
      window.scrollTo({
        top: 0,
        left: 0,
        behavior: 'smooth',
        // eslint-disable-next-line react-hooks/exhaustive-deps
      });
    }
  }, [productList, showModal]);

  // Stop page from scrolling when modal is visible
  React.useEffect(() => {
    if (showModal) {
      document.documentElement.style.overflow = 'hidden';
    } else {
      document.documentElement.style.overflow = '';
    }
  }, [showModal]);

  // Automatically load the next page if "#more" is in the URL
  React.useEffect(() => {
    if (location.hash === '#more') {
      // save state so we can fetch it when the first page got fetched to retrieve the cursor
      shouldLoadNextPage.current = true;
    }

    if (shouldLoadNextPage.current) {
      if (hasNextPage) {
        fetchNextPage();
      }

      shouldLoadNextPage.current = false;
    }
  }, [location.hash]);

  const currencyCode = getCurrencySymbol(
    products?.[0]?.node?.priceRangeV2?.minVariantPrice?.currencyCode,
  );

  return (
    <Layout>
      <Backdrop className={classes.backdrop} open={isFetching}>
        <Typography align="center" gutterBottom color="secondary">
          Buscando...&nbsp;&nbsp;
        </Typography>
        <CircularProgress size={32} color="secondary" />
      </Backdrop>

      <Grid container spacing={4} className={classes.root}>
        <Grid item xs={12} lg={3} className={classes.filters}>
          <Box mt={15}>
            <section
              className={[filterStyle, showModal && modalOpen].join(' ')}
            >
              <div className={filterTitle}>
                <h2>Filtros</h2>
                <button aria-hidden onClick={() => setShowModal(false)}>
                  <CrossIcon />
                </button>
              </div>

              <Filters
                setFilters={setFilters}
                filters={filters}
                tags={tags}
                vendors={vendors}
                productTypes={productTypes}
                currencyCode={currencyCode}
              />
            </section>
          </Box>
        </Grid>
        <Grid
          container
          className={classes.productsContainer}
          spacing={3}
          item
          xs={12}
          lg={9}
        >
          <Grid item xs={12} lg={10}>
            <WhiteSpace />
            <WhiteSpace size="2em" />
            <SearchBar defaultTerm={filters.term} setFilters={setFilters} />
          </Grid>
          <Grid item xs={12} lg={2}>
            <WhiteSpace />
            <WhiteSpace size="2em" />
            <div className={search} aria-hidden={modalOpen}>
              <button
                className={[
                  filterButton,
                  filterCount ? activeFilters : undefined,
                ].join(' ')}
                onClick={() => setShowModal((show) => !show)}
                // This is hidden because the filters are already visible to
                // screenreaders, so the modal isnt needed.
                aria-hidden
              >
                <FilterIcon />
              </button>
            </div>
            <FormControl variant="filled" fullWidth>
              <InputLabel shrink>Ordenar por:</InputLabel>
              <Select
                value={sortKey}
                onChange={(e) => setSortKey(e.target.value)}
              >
                <MenuItem value="RELEVANCE">Relevancia</MenuItem>
                <MenuItem value="PRICE">Precio</MenuItem>
                <MenuItem value="TITLE">Título</MenuItem>
                <MenuItem value="CREATED_AT">Recientes</MenuItem>
                <MenuItem value="BEST_SELLING">Más vistos</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid container item xs={12} lg={12} spacing={3}>
            {!isFetching &&
              productList.map(({ node }, index) => (
                <ProductCard
                  key={node.id}
                  eager={index === 0}
                  product={{
                    title: node.title,
                    priceRangeV2: node.priceRangeV2,
                    slug: `/products/${slugify(node.productType)}/${
                      node.handle
                    }`,
                    // The search API and Gatsby data layer have slightly different images available.
                    images: isDefault ? node.images : [],
                    storefrontImages: !isDefault && node.images,
                    vendor: node.vendor,
                  }}
                />
              ))}
            <section
              className={results}
              aria-busy={isFetching}
              aria-hidden={modalOpen}
            >
              {hasPreviousPage || hasNextPage ? (
                <Pagination
                  previousPage={fetchPreviousPage}
                  hasPreviousPage={hasPreviousPage}
                  nextPage={fetchNextPage}
                  hasNextPage={hasNextPage}
                />
              ) : undefined}
            </section>
          </Grid>
          <br />
          <br />
        </Grid>
      </Grid>
    </Layout>
  );
}

/**
 * Shopify only supports next & previous navigation
 */
function Pagination({ previousPage, hasPreviousPage, nextPage, hasNextPage }) {
  return (
    <nav className={pagination}>
      <button
        className={paginationButton}
        disabled={!hasPreviousPage}
        onClick={previousPage}
        aria-label="Página anterior"
      >
        <CgChevronLeft />
      </button>
      <button
        className={paginationButton}
        disabled={!hasNextPage}
        onClick={nextPage}
        aria-label="Página siguiente"
      >
        <CgChevronRight />
      </button>
    </nav>
  );
}

export default function SearchPageTemplate(props) {
  return (
    <SearchProvider>
      <SearchPage {...props} />
    </SearchProvider>
  );
}
